import { useState } from "react";
import {
  Choices, Details, dimensionError, Disclaimer, NumberField, Nav, Panel, Progress, Question,
  ResultBlock, ResultFooter, ResultHeadline, ResultList, ToolFrame, useTool, useToolEvents, WorkableOptions,
} from "@/components/site/ToolShell";

const SLUG = "sauna-project-planner";

function evaluate(f) {
  const area = (Number(f.width) || 0) * (Number(f.length) || 0);
  const height = Number(f.height) || 0;
  const users = f.users || "2 people";
  const indoor = f.location === "Indoor";

  let headline;
  if (f.type === "Traditional")
    headline = `Your space can support ${indoor ? "an indoor" : "an outdoor"} traditional sauna.`;
  else if (f.type === "Infrared")
    headline = `Your space can support ${indoor ? "an indoor" : "an outdoor"} infrared sauna.`;
  else
    headline = `Your space can support ${indoor ? "an indoor" : "an outdoor"} sauna. You still have flexibility on heater type.`;

  const category =
    f.type === "Infrared"
      ? `${indoor ? "Indoor" : "Outdoor"} infrared cabin`
      : f.type === "Traditional"
      ? `${indoor ? "Indoor" : "Outdoor"} traditional sauna with electric heater`
      : `${indoor ? "Indoor" : "Outdoor"} cabin — traditional or infrared both open`;

  const seatsWanted = { "1 person": 1, "2 people": 2, "3-4 people": 4, "5 or more": 5 }[users] || 2;
  const areaSeats = area < 16 ? 2 : area < 30 ? 3 : area < 48 ? 4 : 6;

  let capacity;
  if (area < 16) capacity = "Realistically one to two seated users";
  else if (area < 30) capacity = "Two to three seated users, or two reclining";
  else if (area < 48) capacity = "Three to four seated users with bench depth for reclining";
  else capacity = "Four or more users depending on bench layout";
  if (seatsWanted > areaSeats)
    capacity += ` — short of the ${users.toLowerCase()} you asked for, so either increase the footprint or plan staggered sessions`;

  // Unknown electrical service means UNVERIFIED, never "extensive work required".
  const unknownService = f.service === "Not sure";
  const electricalStatus = unknownService
    ? "Electrical requirements need verification."
    : f.service === "240V available or easily added"
    ? "240V is available — the full range of heater sizes is open to you."
    : "Only 120V confirmed — heater choice or a new dedicated circuit will decide scope.";

  const electrical = [];
  if (f.type === "Infrared")
    electrical.push("Many infrared cabins are designed for a standard 120V circuit, but larger cabins can exceed it. Confirm the requirement for the specific model.");
  else if (f.type === "Traditional")
    electrical.push("Traditional electric heaters are sized to the sauna's volume and to the specific heater's rating. Many residential traditional electric heaters require 240V hardwired service, but exact voltage, amperage and heater requirements remain model-specific.");
  else
    electrical.push("Heater type drives the electrical requirement. Traditional electric heaters generally need more capacity than infrared cabins of similar size, and the exact requirement is model-specific.");
  if (unknownService)
    electrical.push("You have not checked the panel yet, so the electrical requirement is simply unverified — not necessarily large. An electrician can confirm what your panel supports in a single visit, and that one check resolves most of the uncertainty in a sauna project.");
  else if (f.service === "Only standard 120V outlets available")
    electrical.push("With only 120V confirmed, either choose a model designed for a standard circuit or budget for a dedicated circuit run by a licensed electrician.");
  else
    electrical.push("Confirm breaker and conductor sizing with your electrician against the heater's published requirement.");
  if (!indoor) electrical.push("Outdoor cabins add distance, conduit or burial requirements and wet-location wiring considerations to the supply run.");

  // Scope must not be inflated purely because the service is unknown.
  let scope;
  if (unknownService) scope = "Scope not yet determined — pending an electrical check";
  else if (f.type === "Infrared" && f.service !== "Only standard 120V outlets available")
    scope = "Contained scope — cabin-led project with limited site work";
  else if (f.service === "240V available or easily added")
    scope = "Contained to moderate scope — cabin plus straightforward electrical";
  else scope = "Moderate scope — cabin plus a dedicated circuit and site preparation";
  if (!indoor && !unknownService) scope += ", plus a level base and an exterior supply run";

  const install = [];
  install.push("Measure doorways, stairs, turns and the delivery route before ordering — access defeats more sauna projects than space does.");
  if (indoor) install.push("Indoor installations need a moisture-tolerant floor, a plan for water, and real intake and exhaust ventilation — not just an open door.");
  else install.push("Outdoor cabins need a level base or pad and exterior finish maintenance on the manufacturer's schedule.");
  if (height && height < 7)
    install.push(`Your available ceiling height (${height} ft) is on the low side — confirm the cabin's published exterior height and the heater's required clearance to the ceiling.`);
  else if (height)
    install.push(`Confirm the cabin's published exterior height fits your ${height} ft ceiling, including heater clearance above the stones.`);
  install.push("Electrical work and heater clearances must follow the published installation instructions and be verified by a licensed electrician.");

  const costs = ["Cabin or kit", "Heater and stones if not bundled"];
  if (f.service === "Only standard 120V outlets available" && f.type !== "Infrared")
    costs.push("Dedicated circuit run, breaker and electrician labour");
  if (unknownService) costs.push("Electrician's visit to confirm what the panel supports");
  if (!indoor) costs.push("Level pad or base", "Exterior electrical run (conduit or burial)");
  if (indoor) costs.push("Moisture-appropriate flooring", "Ventilation work");
  costs.push("Delivery and placement", "Permit where required by your jurisdiction");

  const reasons = [
    `Your ${(Number(f.width) || 0)} × ${(Number(f.length) || 0)} ft footprint supports ${capacity.toLowerCase().split(" — ")[0]}.`,
    electricalStatus,
  ];
  if (f.type === "Undecided") reasons.push("Heater type is still open, so both traditional and infrared paths remain available to you.");

  // Workable directions derived from the values already calculated above. Presentation only.
  const workable = [];
  const w = Number(f.width) || 0;
  const l = Number(f.length) || 0;
  if (f.type === "Traditional")
    workable.push(`${indoor ? "Indoor" : "Outdoor"} traditional cabin sized to your ${w} × ${l} ft footprint`);
  else if (f.type === "Infrared")
    workable.push(`${indoor ? "Indoor" : "Outdoor"} infrared cabin sized to your ${w} × ${l} ft footprint`);
  else {
    workable.push(`${indoor ? "Indoor" : "Outdoor"} traditional cabin sized to your ${w} × ${l} ft footprint`);
    workable.push(`${indoor ? "Indoor" : "Outdoor"} infrared cabin sized to the same footprint`);
  }
  workable.push(`Bench layout for ${capacity.toLowerCase().split(" — ")[0]}`);
  if (f.service === "Only standard 120V outlets available")
    workable.push("Cabins designed for a standard 120V circuit, or the same cabin plus a dedicated circuit");
  else if (f.service === "240V available or easily added")
    workable.push("The full range of heater sizes, including 240V hardwired heaters");
  else
    workable.push("Both 120V and 240V heater paths stay open until an electrician confirms your panel");
  if (!indoor) workable.push("A level pad or base and an exterior supply run as part of the project");

  const optionsHeadline = "Sauna configurations your space supports";

  return { headline, category, capacity, electricalStatus, electrical, install, costs, scope, reasons, workable, optionsHeadline };
}

const INITIAL = { location: "Indoor", type: "Undecided", users: "2 people", service: "Not sure" };

export default function SaunaPlanner() {
  const tool = useTool(SLUG);
  const { start, complete, resetRun } = useToolEvents(SLUG);
  const [step, setStep] = useState(0);
  const [f, setF] = useState(INITIAL);
  const [showErrors, setShowErrors] = useState(false);
  const [result, setResult] = useState(null);
  const set = (k) => (v) => { start(); setF((s) => ({ ...s, [k]: v })); };
  const total = 3;

  const errs = {
    width: dimensionError(f.width, { min: 3, max: 30 }),
    length: dimensionError(f.length, { min: 3, max: 30 }),
    height: dimensionError(f.height, { min: 5, max: 15 }),
  };
  const stepValid = step === 0 ? !errs.width && !errs.length && !errs.height : true;

  const next = () => {
    start();
    if (!stepValid) return setShowErrors(true);
    setShowErrors(false);
    if (step < total - 1) return setStep(step + 1);
    const r = evaluate(f);
    setResult(r);
    complete({ category: r.category });
  };

  if (!tool) return <div className="h-[60vh]" />;

  return (
    <ToolFrame tool={tool}>
      <Panel>
        {!result ? (
          <>
            <Progress step={step} total={total} />

            {step === 0 && (
              <>
                <Question label="Indoor or outdoor?">
                  <Choices testId="choice-location" options={["Indoor", "Outdoor"]} value={f.location} onChange={set("location")} />
                </Question>
                <Question label="What space do you have available?" hint="Interior footprint of the cabin area, not the whole room.">
                  <div className="grid sm:grid-cols-3 gap-4">
                    <NumberField label="Width" unit="ft" value={f.width} onChange={set("width")} testId="input-sauna-width" placeholder="e.g. 5" error={errs.width} showError={showErrors} />
                    <NumberField label="Depth" unit="ft" value={f.length} onChange={set("length")} testId="input-sauna-length" placeholder="e.g. 6" error={errs.length} showError={showErrors} />
                    <NumberField label="Ceiling height" unit="ft" value={f.height} onChange={set("height")} testId="input-sauna-height" placeholder="e.g. 7" error={errs.height} showError={showErrors} />
                  </div>
                </Question>
              </>
            )}

            {step === 1 && (
              <>
                <Question label="How many people will use it at once?">
                  <Choices testId="choice-users" options={["1 person", "2 people", "3-4 people", "5 or more"]} value={f.users} onChange={set("users")} />
                </Question>
                <Question
                  label="Traditional, infrared or undecided?"
                  hint="Traditional heats the room and lets you use water on stones. Infrared heats the body at lower air temperatures."
                >
                  <Choices
                    testId="choice-type"
                    options={[
                      { value: "Traditional", label: "Traditional", note: "Higher air temperature, steam possible" },
                      { value: "Infrared", label: "Infrared", note: "Lower air temperature, faster warm-up" },
                      { value: "Undecided", label: "Undecided", note: "Show me what each path means" },
                    ]}
                    value={f.type}
                    onChange={set("type")}
                  />
                </Question>
              </>
            )}

            {step === 2 && (
              <Question
                label="What electrical service is available where the sauna will go?"
                hint="If you have not checked, say so — that is a normal answer and it does not mean a big job."
              >
                <Choices
                  testId="choice-service"
                  cols={1}
                  options={[
                    { value: "Only standard 120V outlets available", label: "Only standard 120V outlets available" },
                    { value: "240V available or easily added", label: "240V available, or the panel is close and has capacity" },
                    { value: "Not sure", label: "Not sure — I haven't checked my electrical panel" },
                  ]}
                  value={f.service}
                  onChange={set("service")}
                />
              </Question>
            )}

            <Nav step={step} total={total} onBack={() => { setShowErrors(false); setStep(Math.max(0, step - 1)); }} onNext={next} disabled={!stepValid} />
          </>
        ) : (
          <div data-testid="tool-result">
            <ResultHeadline
              eyebrow="Your sauna project plan"
              headline={result.headline}
              reasons={result.reasons}
              testId="result-headline"
            />
            <WorkableOptions
              tool={tool}
              headline={result.optionsHeadline}
              options={result.workable}
              productPath="Home sauna cabins, kits and heaters matched to your space and electrical service"
            />
            <div className="border-t-2 border-charcoal mt-12">
              <ResultBlock label="Space fit" value={result.capacity} testId="result-capacity" />
              <ResultBlock label="Likely direction" value={result.category} testId="result-category" />
              <ResultBlock label="Electrical status" value={result.electricalStatus} testId="result-electrical-status" />
              <ResultBlock label="Project scope" value={result.scope} emphasis testId="result-scope" />
              <Details label="See installation and electrical details" testId="result-details">
                <ResultList label="Electrical considerations" items={result.electrical} testId="result-electrical" />
                <ResultList label="Installation considerations" items={result.install} testId="result-install" />
                <ResultList label="Project cost line items to budget for" items={result.costs} testId="result-costs" />
              </Details>
            </div>

            <Disclaimer>
              This is planning guidance, not an electrical design or a quote. Heater sizing depends on
              sauna volume and the specific heater's rating, and exact voltage, amperage and clearance
              requirements are model-specific. Electrical work must be verified by a qualified
              electrician against the manufacturer's published installation requirements.
            </Disclaimer>

            <div className="mt-2">
              <ResultFooter onReset={() => { resetRun(); setResult(null); setStep(0); setF(INITIAL); setShowErrors(false); }} />
            </div>
          </div>
        )}
      </Panel>
    </ToolFrame>
  );
}
